import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session-48g — Chat Header Labels admin settings.
 * Session-49  — Rewritten with explicit "Save Changes" button + live socket
 *               broadcast. Prior version auto-saved on blur which felt fragile
 *               (accidentally saving typos on tab-away) and never reached the
 *               connected chat headers instantly. Now:
 *                 • all edits stay in `pending` state
 *                 • floating "Save Changes (N)" button only enables when the
 *                   user actually changed something
 *                 • one bulk POST → backend emits `header_labels_updated`
 *                   which ChatHeaderActions listens to → headers live-update.
 */
const DEFAULTS = {
  deposit:  { handler:  { left_create: 'Create Deposit Form',  right_action: 'Deposit' },
              admin:    { left_create: 'Create Deposit Form',  right_action: 'Deposit' },
              customer: { left_create: 'Deposit',              right_action: 'Deposit Details' } },
  withdraw: { handler:  { left_create: 'Create Withdraw Form', right_action: 'Coin Cut' },
              admin:    { left_create: 'Create Withdraw Form', right_action: 'Coin Cut' },
              customer: { left_create: 'Withdrawal Form',      right_action: '' } },
  support:  { handler:  { left_create: 'Create Support R',     right_action: 'Mark as Solved' },
              admin:    { left_create: 'Create Support R',     right_action: 'Mark as Solved' },
              customer: { left_create: 'Support R',            right_action: '' } },
};

const HeaderLabelsSettings = ({ adminId }) => {
  const [labels, setLabels] = useState({});       // committed / server-side
  const [pending, setPending] = useState({});     // uncommitted edits keyed by chat:role
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/header-labels/all`);
      const map = {};
      (r.data?.labels || []).forEach(l => { map[`${l.chat_type}:${l.role}`] = l; });
      setLabels(map);
      setPending({});
    } catch { toast.error('Failed to load labels'); }
    finally { setLoading(false); }
  };

  // Build the effective value for an input — pending edit wins over committed.
  const effective = (ct, role, field) => {
    const key = `${ct}:${role}`;
    const p = pending[key];
    if (p && field in p) return p[field];
    return labels[key]?.[field] || '';
  };

  const edit = (ct, role, field, value) => {
    const key = `${ct}:${role}`;
    setPending(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), chat_type: ct, role, [field]: value },
    }));
  };

  const changedCount = useMemo(() => {
    let n = 0;
    Object.entries(pending).forEach(([k, patch]) => {
      const committed = labels[k] || {};
      ['left_create', 'right_action', 'customer_mode'].forEach(f => {
        if (f in patch && (patch[f] || '') !== (committed[f] || '')) n += 1;
      });
    });
    return n;
  }, [pending, labels]);

  const saveAll = async () => {
    if (changedCount === 0) return;
    setSaving(true);
    try {
      const items = Object.values(pending);
      await axios.post(`${API}/admin/header-labels/bulk`, { items });
      toast.success(`✅ Saved ${changedCount} change${changedCount === 1 ? '' : 's'} — customer & handler headers updated live`);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSaving(false); }
  };

  const discardAll = () => {
    if (changedCount === 0) return;
    setPending({});
    toast.message('Changes discarded');
  };

  if (loading) return <div className="p-4 text-slate-500">Loading…</div>;

  const chatTypes = ['deposit', 'withdraw', 'support'];
  const roles = ['handler', 'admin', 'customer'];

  return (
    <div className="p-4 max-w-3xl pb-32">
      <h2 className="font-bold text-lg mb-1">🏷️ Chat Header Labels</h2>
      <p className="text-xs text-slate-500 mb-4">
        Har chat type ke top-header buttons ke labels admin yahan rename kar sakta hai.
        Empty rakh do to app apna default use karega. Change karke <b>Save Changes</b> dabao —
        turant customer + handler + admin sab connected chats mein reflect ho jayega.
      </p>

      {chatTypes.map(ct => (
        <div key={ct} className="mb-5 border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-100 px-3 py-2 font-bold text-sm capitalize">
            {ct === 'withdraw' ? 'Withdraw' : ct} Chat
          </div>
          <div className="p-3 space-y-3">
            {roles.map(role => {
              const def = DEFAULTS[ct][role];
              const isCustomer = role === 'customer';
              const key = `${ct}:${role}`;
              const isDirty = !!pending[key];
              return (
                <div
                  key={role}
                  className={`border rounded p-2 bg-white transition ${
                    isDirty ? 'border-amber-400 bg-amber-50/40' : 'border-slate-200'
                  }`}
                >
                  <div className="text-xs font-semibold text-slate-700 mb-1.5 capitalize flex items-center justify-between">
                    <span>{role} view</span>
                    {isDirty && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded font-normal">
                        unsaved
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-500">
                        Left button (default: <code>{def.left_create}</code>)
                      </label>
                      <input
                        type="text"
                        placeholder={def.left_create}
                        value={effective(ct, role, 'left_create')}
                        onChange={(e) => edit(ct, role, 'left_create', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                        data-testid={`hdr-${ct}-${role}-left`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">
                        Right button (default: <code>{def.right_action || '—'}</code>)
                      </label>
                      <input
                        type="text"
                        placeholder={def.right_action || 'Right label'}
                        value={effective(ct, role, 'right_action')}
                        onChange={(e) => edit(ct, role, 'right_action', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                        data-testid={`hdr-${ct}-${role}-right`}
                      />
                    </div>
                  </div>
                  {isCustomer && (
                    <div className="mt-2">
                      <label className="text-[10px] text-slate-500">Mode</label>
                      <select
                        value={effective(ct, role, 'customer_mode') || 'split'}
                        onChange={(e) => edit(ct, role, 'customer_mode', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                        data-testid={`hdr-${ct}-${role}-mode`}
                      >
                        <option value="split">Split (2 buttons)</option>
                        <option value="single">Single (1 button)</option>
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Floating Save bar — sticks to the bottom of the settings panel */}
      <div className="fixed bottom-0 left-0 right-0 sm:left-auto sm:right-4 sm:max-w-md bg-white border-t sm:border sm:rounded-lg sm:shadow-lg p-3 flex items-center justify-between gap-3 z-40">
        <div className="text-xs text-slate-600">
          {changedCount === 0
            ? 'No unsaved changes'
            : <><b className="text-amber-700">{changedCount}</b> unsaved change{changedCount === 1 ? '' : 's'}</>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={discardAll}
            disabled={changedCount === 0 || saving}
            className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-700 disabled:opacity-40"
            data-testid="hdr-labels-discard"
          >
            Discard
          </button>
          <button
            onClick={saveAll}
            disabled={changedCount === 0 || saving}
            className="px-4 py-1.5 text-xs rounded bg-emerald-600 text-white font-semibold disabled:opacity-40"
            data-testid="hdr-labels-save"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeaderLabelsSettings;
